/**
 * Integrytas Group — Record Extensions: LIST hydration.
 *
 * Companion to useRecordExtensionForm (which drives the edit modal). Given a host
 * page's code and its already-loaded rows, this returns everything the list needs
 * to render every registered extension:
 *   - columns       : GridColumnDef[] to append to the host's PersonalizedGrid
 *   - facets        : multi-value filter facets (§17.B5), labelled not coded
 *   - renderCell    : the cell node for an extension column key
 *   - relatedGroups : extra cards for the related-children panel (§17.B13)
 *   - searchText    : extension text folded into the host's free-text filter
 *
 * Deliberately shaped like useUdcGrid so a host page wires both the same way.
 * With no extension registered (BaseApp itself, and any app that declares none)
 * every list is empty and no query is issued — the hook is completely inert.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useActiveLocale } from '../lib/use_active_locale';
import {
  extColumnKey,
  getRecordExtensions,
  parseExtColumnKey,
  type ExtensionField,
  type RecordExtension,
} from '../lib/record_extensions';
import {
  extensionCellText,
  extensionCompare,
  loadExtensionListData,
  type ExtensionListData,
} from '../lib/record_extension_data';
import { YesNoBadge } from '../components/common/YesNoBadge';
import type { GridColumnDef } from '../components/personalization';
import type { FacetOption } from '../components/common/FacetFilter';
import type { RelatedChildGroup } from '../components/common/RelatedChildrenPanel';

export interface ExtFacetDef { key: string; label: string; options: FacetOption[] }

export interface UseRecordExtensions {
  columns: GridColumnDef[];
  facets: ExtFacetDef[];
  isExtKey: (key: string) => boolean;
  renderCell: (row: Record<string, unknown>, key: string) => React.ReactNode;
  compare: (a: Record<string, unknown>, b: Record<string, unknown>, key: string) => number;
  matchRow: (row: Record<string, unknown>) => boolean;
  /** Concatenated extension text for the host's free-text filter. */
  searchText: (row: Record<string, unknown>) => string;
  relatedGroups: (pk: string | number) => RelatedChildGroup[];
  hasRelated: (pk: string | number) => boolean;
  filterValues: Record<string, string[]>;
  setFilter: (key: string, values: string[]) => void;
  clear: () => void;
  hasActiveFilters: boolean;
  loading: boolean;
  /** Re-read every extension (call after the host saves). */
  reload: () => void;
}

interface Opts {
  /** The host entity's pk column, e.g. 'user_id'. */
  pkColumn: string;
}

type DataByExt = Record<string, ExtensionListData>;

const NO_DATA: ExtensionListData = { rows: {}, labels: {}, children: {} };

export function useRecordExtensions(pageCode: string, opts: Opts): UseRecordExtensions {
  const { pkColumn } = opts;
  const { t } = useTranslation();
  const locale = useActiveLocale();
  const [data, setData] = useState<DataByExt>({});
  const [filterValues, setFilterValues] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(false);
  const [nonce, setNonce] = useState(0);

  // The registry is populated at module load, so the set is stable for a page.
  const exts = useMemo(() => getRecordExtensions(pageCode), [pageCode]);

  useEffect(() => {
    if (exts.length === 0) { setData({}); return; }
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const loaded = await Promise.all(exts.map(async e => [e.key, await loadExtensionListData(e, locale)] as const));
      if (cancelled) return;
      setData(Object.fromEntries(loaded));
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [exts, locale, nonce]);

  /** Manifest field behind a prefixed column key, with its owning extension. */
  const fieldOf = useCallback((key: string): { ext: RecordExtension; field: ExtensionField } | null => {
    const parsed = parseExtColumnKey(key);
    if (!parsed) return null;
    const ext = exts.find(e => e.key === parsed.ext_key);
    const field = ext?.fields.find(f => f.key === parsed.field_key);
    return ext && field ? { ext, field } : null;
  }, [exts]);

  /** Raw sister-table value for a host row + prefixed column key. */
  const valueOf = useCallback((row: Record<string, unknown>, key: string): unknown => {
    const found = fieldOf(key);
    if (!found) return undefined;
    const sister = (data[found.ext.key] ?? NO_DATA).rows[String(row[pkColumn])];
    return sister?.[found.field.key];
  }, [data, fieldOf, pkColumn]);

  const columns: GridColumnDef[] = useMemo(
    () => exts.flatMap(e => e.fields.filter(f => f.grid).map(f => ({
      key: extColumnKey(e.key, f.key),
      header: t(f.label_key, f.label_default),
      defaultWidth: f.grid_width ?? 150,
    }))),
    [exts, t],
  );

  const facets: ExtFacetDef[] = useMemo(() => exts.flatMap(e => {
    const d = data[e.key] ?? NO_DATA;
    return e.fields.filter(f => f.facet).map(f => {
      const label = t(f.label_key, f.label_default);
      const key = extColumnKey(e.key, f.key);
      if (f.kind === 'yn') {
        return { key, label, options: [
          { value: 'Y', label: t('common.boolean.yes', 'Yes') },
          { value: 'N', label: t('common.boolean.no', 'No') },
        ] };
      }
      // Options carry the RAW value but a RESOLVED label (§17.B4 — a facet whose
      // option label is a bare code is as non-conformant as a grid cell showing one).
      const seen = new Set<string>();
      for (const sister of Object.values(d.rows)) {
        const raw = sister[f.key];
        if (raw !== null && raw !== undefined && raw !== '') seen.add(String(raw));
      }
      const options: FacetOption[] = [...seen]
        .map(v => ({ value: v, label: extensionCellText(f, v, d.labels[f.key]) || v }))
        .sort((a, b) => a.label.localeCompare(b.label));
      return { key, label, options };
    });
  }), [exts, data, t]);

  const isExtKey = useCallback((key: string) => fieldOf(key) !== null, [fieldOf]);

  const renderCell = useCallback((row: Record<string, unknown>, key: string): React.ReactNode => {
    const found = fieldOf(key);
    if (!found) return null;
    const raw = valueOf(row, key);
    if (found.field.kind === 'yn') {
      return raw === 'Y' || raw === 'N'
        ? <YesNoBadge value={raw} yesLabel={t('common.boolean.yes', 'Yes')} noLabel={t('common.boolean.no', 'No')} />
        : null;
    }
    return extensionCellText(found.field, raw, (data[found.ext.key] ?? NO_DATA).labels[found.field.key]);
  }, [fieldOf, valueOf, data, t]);

  const compare = useCallback((a: Record<string, unknown>, b: Record<string, unknown>, key: string): number => {
    const found = fieldOf(key);
    if (!found) return 0;
    const labels = (data[found.ext.key] ?? NO_DATA).labels[found.field.key];
    return extensionCompare(found.field, valueOf(a, key), valueOf(b, key), labels);
  }, [fieldOf, valueOf, data]);

  const matchRow = useCallback((row: Record<string, unknown>): boolean => {
    for (const [key, selected] of Object.entries(filterValues)) {
      if (!selected || selected.length === 0) continue;
      const raw = valueOf(row, key);
      if (!selected.includes(String(raw ?? ''))) return false;
    }
    return true;
  }, [filterValues, valueOf]);

  const searchText = useCallback((row: Record<string, unknown>): string => {
    const parts: string[] = [];
    for (const e of exts) {
      const d = data[e.key] ?? NO_DATA;
      const sister = d.rows[String(row[pkColumn])];
      if (!sister) continue;
      for (const f of e.fields) {
        const text = extensionCellText(f, sister[f.key], d.labels[f.key]);
        if (text) parts.push(text);
      }
    }
    return parts.join(' ').toLowerCase();
  }, [exts, data, pkColumn]);

  const relatedGroups = useCallback((pk: string | number): RelatedChildGroup[] =>
    exts.flatMap(e => {
      const d = data[e.key] ?? NO_DATA;
      return (e.children ?? [])
        .filter(c => c.kind !== 'address')
        .map(c => ({
          key: `${e.key}:${c.key}`,
          title: t(c.label_key, c.label_default),
          items: d.children[c.key]?.[String(pk)] ?? [],
        }))
        .filter(g => g.items.length > 0);
    }), [exts, data, t]);

  const hasRelated = useCallback((pk: string | number) => relatedGroups(pk).length > 0, [relatedGroups]);

  const setFilter = useCallback((key: string, values: string[]) => {
    setFilterValues(prev => ({ ...prev, [key]: values }));
  }, []);
  const clear = useCallback(() => setFilterValues({}), []);
  const hasActiveFilters = useMemo(
    () => Object.values(filterValues).some(v => v && v.length > 0),
    [filterValues],
  );
  const reload = useCallback(() => setNonce(n => n + 1), []);

  // Memoised so the returned OBJECT is as stable as its members already are
  // (every one is useMemo/useCallback/useState). Same reasoning as useUdcGrid:
  // a fresh literal changes identity every render, which forces call sites to
  // list individual members in their dependency arrays — precise, but easy to
  // get incomplete. With a stable object, `[…, ext]` is both correct and cheap.
  return useMemo(
    () => ({
      columns, facets, isExtKey, renderCell, compare, matchRow, searchText,
      relatedGroups, hasRelated, filterValues, setFilter, clear, hasActiveFilters,
      loading, reload,
    }),
    [
      columns, facets, isExtKey, renderCell, compare, matchRow, searchText,
      relatedGroups, hasRelated, filterValues, setFilter, clear, hasActiveFilters,
      loading, reload,
    ],
  );
}
